import { useEffect, useRef, useState } from "react";
import Recorder from "./Recorder";
import { api } from "../lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Wand2, Loader2, SkipForward, ClipboardCheck, FilePlus2, Trash2, FileClock, Check } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export const ABOUT_FIELDS = [
  ["Full name *", "member_name", "text"], ["Date of birth", "date_of_birth", "date"],
  ["Medicaid ID", "member_id", "text"], ["Record #", "record_number", "text"],
  ["Diagnosis", "diagnosis", "text"], ["PCP completed on", "plan_date", "date"],
  ["Plan meeting date", "plan_meeting_date", "date"], ["Effective date", "effective_date", "date"],
];

export const STEPS = [
  { key: "about", type: "fields", section: "About the person", question: "Let's start with the basics.", hint: "Type these exactly as they should print on the plan. Only the name is required." },
  { key: "domain_daily_life", section: "Life Domain 1 of 6", question: "Tell me about their Daily Life and Employment.", hint: "How do they spend their days? Work, volunteering, school, PSR attendance, routines, what they enjoy doing." },
  { key: "domain_community_living", section: "Life Domain 2 of 6", question: "Tell me about their Community Living.", hint: "Where do they live and with whom? Getting around town, shopping, money management, cooking, housekeeping." },
  { key: "domain_safety_security", section: "Life Domain 3 of 6", question: "Tell me about their Safety and Security.", hint: "Safety concerns, past crises, legal issues, housing stability, whether they know who to call for help." },
  { key: "domain_healthy_living", section: "Life Domain 4 of 6", question: "Tell me about their Healthy Living.", hint: "Physical health, medications and how they manage them, sleep, substance use, exercise, doctors." },
  { key: "domain_social_spirituality", section: "Life Domain 5 of 6", question: "Tell me about their Social life and Spirituality.", hint: "Family, friends, peers, relationships, faith community, how they socialize, loneliness." },
  { key: "domain_citizenship_advocacy", section: "Life Domain 6 of 6", question: "Tell me about Citizenship and Advocacy.", hint: "Voting, speaking up for themselves, self-advocacy, community groups, rights, guardianship." },
  { key: "want_to_work_on", section: "In their own words", question: "What does the person say they want to work on or accomplish?", hint: "Use their own words as much as you can — \"I want to…\"" },
  { key: "strengths", section: "In their own words", question: "What strengths do they say they have?", hint: "What are they good at? What do they like about themselves? What is going well?" },
  { key: "obstacles", section: "In their own words", question: "What do they say gets in the way of their goals?", hint: "Barriers, worries, symptoms, money, transportation, relationships…" },
  { key: "long_range_goal", section: "Action Plan", question: "What is the big-picture Long-Term Goal for the next 12 months?", hint: "One sentence describing where they want to be in a year." },
  { key: "short_term_goals", section: "Action Plan", question: "What specific skills should they build in the next 6 months?", hint: "Name 2–4 skills (community living, personal care, social, leisure, education, prevocational). I'll turn each into a SMART goal." },
  { key: "staff_interventions", section: "Action Plan", question: "What will PSR staff (and group home staff, if any) do to teach those skills?", hint: "Teaching, coaching, modeling, practice — how often and how long. Say if they live in a group or family care home." },
  { key: "natural_supports", section: "Action Plan", question: "What will the person and their natural supports do?", hint: "What the person commits to, and who helps them outside of staff (family, friends, church, peers) and how." },
  { key: "crisis", section: "Crisis plan", question: "What should happen in a crisis?", hint: "Triggers and warning signs, what prevents a crisis, what calms them down, who to call, and follow-up afterwards." },
  { key: "team", section: "Team & signatures", question: "Who is on the team and who signs this plan?", hint: "Say each person's name and role — the member, legally responsible person/guardian, the QP, staff, family. Each gets their own signature line." },
];
const QUESTIONS = STEPS.filter((s) => s.type !== "fields");

const LABELS = {
  domain_daily_life: "LIFE DOMAIN — Daily Life and Employment", domain_community_living: "LIFE DOMAIN — Community Living",
  domain_safety_security: "LIFE DOMAIN — Safety and Security", domain_healthy_living: "LIFE DOMAIN — Healthy Living",
  domain_social_spirituality: "LIFE DOMAIN — Social and Spirituality", domain_citizenship_advocacy: "LIFE DOMAIN — Citizenship and Advocacy",
  want_to_work_on: "PERSON'S OWN WORDS — What do you want to work on / accomplish?", strengths: "PERSON'S OWN WORDS — What strengths do you currently have?",
  obstacles: "PERSON'S OWN WORDS — What are the obstacles to meeting your goals?", long_range_goal: "LONG-TERM GOAL (12 months)",
  short_term_goals: "SHORT-TERM GOALS — skills to build in the next 6 months", staff_interventions: "INTERVENTIONS — what PSR staff / group home staff will do",
  natural_supports: "INTERVENTIONS — what the individual and natural supports will do", crisis: "CRISIS PLAN (triggers, prevention, stabilization, contacts, follow-up)",
  team: "TEAM MEMBERS / RESPONSIBLE PARTIES for signatures (name — role)",
};

export function buildInterviewText(about, answers) {
  const head = ABOUT_FIELDS.filter(([, k]) => about[k]).map(([l, k]) => `${l.replace(" *", "")}: ${about[k]}`).join(". ");
  const parts = [`ABOUT THE PERSON: ${head || "(not provided)"}`];
  QUESTIONS.forEach((s) => parts.push(`${LABELS[s.key]}:\n${(answers[s.key] || "").trim() || "(skipped)"}`));
  return parts.join("\n\n");
}

const timeAgo = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

function DraftPicker({ drafts, onResume, onNew, onDelete }) {
  return (
    <div data-testid="draft-picker" className="p-6 sm:p-10">
      <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-[0.2em] flex items-center gap-1.5"><FileClock className="w-3.5 h-3.5" strokeWidth={2} /> Saved drafts</p>
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814] mt-1">Pick up where you left off?</h2>
      <p className="mt-2 text-sm text-[#7A8C82]">Your unfinished interviews are saved automatically.</p>
      <div className="mt-6 space-y-2 max-w-2xl">
        {drafts.map((d) => (
          <div key={d.id} data-testid={`draft-row-${d.id}`} className="flex items-center gap-3 p-4 rounded-xl border border-[#E2DFD8] bg-[#F9F9F6]">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#121814] truncate">{d.member_name || "Unnamed person"}</p>
              <p className="text-xs text-[#7A8C82]">{d.answered} of {QUESTIONS.length} answered · saved {timeAgo(d.updated_at)}</p>
            </div>
            <Button data-testid={`resume-draft-${d.id}`} onClick={() => onResume(d)} className="h-10 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">Resume</Button>
            <button data-testid={`delete-draft-${d.id}`} onClick={() => onDelete(d)} className="h-10 w-10 rounded-full border border-[#E2DFD8] text-[#B0503C] hover:bg-[#FBF2EF] flex items-center justify-center" title="Discard draft">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
      <Button data-testid="new-interview-btn" variant="outline" onClick={onNew} className="mt-6 h-12 px-6 rounded-full border-[#4A6B53] text-[#4A6B53] bg-white">
        <FilePlus2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Start a new PCP instead
      </Button>
    </div>
  );
}

function ReviewScreen({ about, answers, setAbout, setAnswers, onJump, onBack, onBuild, generating }) {
  const answered = QUESTIONS.filter((q) => (answers[q.key] || "").trim()).length;
  return (
    <div data-testid="interview-review" className="p-6 sm:p-10">
      <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-[0.2em] flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" strokeWidth={2} /> Review before building</p>
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814] mt-1">Everything look right?</h2>
      <p className="mt-2 text-sm text-[#7A8C82]">{answered} of {QUESTIONS.length} questions answered. Fix anything right here, then build the PCP.</p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {ABOUT_FIELDS.map(([label, key, type]) => (
          <div key={key} className="space-y-1">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</Label>
            <Input data-testid={`review-${key}`} type={type} value={about[key] || ""} onChange={(e) => setAbout((a) => ({ ...a, [key]: e.target.value }))} className="h-10 bg-[#F9F9F6] border-[#E2DFD8] text-sm" />
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {QUESTIONS.map((q, i) => {
          const empty = !(answers[q.key] || "").trim();
          return (
            <div key={q.key} data-testid={`review-item-${q.key}`} className={`rounded-xl border p-4 ${empty ? "border-dashed border-[#E2DFD8] bg-white" : "border-[#DCE5DF] bg-[#F9F9F6]"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">{q.section}</p>
                  <p className="text-sm font-semibold text-[#121814]">{q.question}</p>
                </div>
                <button data-testid={`review-jump-${q.key}`} onClick={() => onJump(i + 1)} className="text-xs font-semibold text-[#4A6B53] hover:underline shrink-0">Go to question</button>
              </div>
              <Textarea data-testid={`review-${q.key}`} value={answers[q.key] || ""} rows={empty ? 2 : 3} placeholder="Skipped — type an answer or leave blank"
                onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))} className="mt-2 bg-white border-[#E2DFD8] text-sm leading-relaxed" />
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
        <Button data-testid="review-back-btn" variant="outline" onClick={onBack} disabled={generating} className="h-12 px-5 rounded-full border-[#E2DFD8] bg-white">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back to questions
        </Button>
        <Button data-testid="interview-build-btn" onClick={onBuild} disabled={generating || !(about.member_name || "").trim()}
          className="h-12 px-8 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-base font-medium shadow-[0_8px_16px_rgba(74,107,83,0.25)]">
          {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Building PCP…</> : <><Wand2 className="w-5 h-5 mr-2" strokeWidth={1.5} /> Build PCP</>}
        </Button>
      </div>
    </div>
  );
}

export default function PcpInterview({ onComplete, generating }) {
  const [phase, setPhase] = useState("loading"); // loading | pick | interview | review
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [step, setStep] = useState(0);
  const [about, setAbout] = useState({ plan_date: today() });
  const [answers, setAnswers] = useState({});
  const dirty = useRef(false);
  const saving = useRef(false);

  useEffect(() => {
    api.get("/pcp/drafts").then(({ data }) => { setDrafts(data); setPhase(data.length ? "pick" : "interview"); }).catch(() => setPhase("interview"));
  }, []);

  useEffect(() => { dirty.current = true; }, [about, answers, step]);

  const draftRef = useRef(null);
  useEffect(() => { draftRef.current = draftId; }, [draftId]);

  useEffect(() => {
    if (phase === "loading" || phase === "pick" || !(about.member_name || "").trim()) return;
    const t = setTimeout(async () => {
      if (!dirty.current || saving.current) return;
      saving.current = true;
      try {
        const { data } = await api.put("/pcp/drafts", { id: draftRef.current, about, answers, step });
        setDraftId(data.id);
        setSavedAt(data.updated_at);
        dirty.current = false;
      } catch { /* retry on next change */ } finally { saving.current = false; }
    }, 1200);
    return () => clearTimeout(t);
  }, [about, answers, step, phase]);

  const resume = (d) => { setAbout({ plan_date: today(), ...(d.about || {}) }); setAnswers(d.answers || {}); setStep(Math.min(d.step || 0, STEPS.length - 1)); setDraftId(d.id); setPhase("interview"); };
  const deleteDraft = async (d) => {
    try { await api.delete(`/pcp/drafts/${d.id}`); } catch { /* ignore */ }
    const rest = drafts.filter((x) => x.id !== d.id);
    setDrafts(rest);
    if (!rest.length) setPhase("interview");
    toast.success("Draft discarded.");
  };

  const s = STEPS[step];
  const last = step === STEPS.length - 1;
  const answered = QUESTIONS.filter((x) => (answers[x.key] || "").trim()).length;
  const nameOk = !!(about.member_name || "").trim();
  const next = () => { if (last) setPhase("review"); else setStep(step + 1); };
  const build = () => onComplete(buildInterviewText(about, answers), about, draftId);
  const append = (t) => setAnswers((a) => ({ ...a, [s.key]: a[s.key] ? a[s.key] + " " + t : t }));

  if (phase === "loading") return <div className="p-10 text-sm text-[#7A8C82]">Loading…</div>;
  if (phase === "pick") return <DraftPicker drafts={drafts} onResume={resume} onDelete={deleteDraft} onNew={() => setPhase("interview")} />;
  if (phase === "review") return <ReviewScreen about={about} answers={answers} setAbout={setAbout} setAnswers={setAnswers} generating={generating}
    onJump={(i) => { setStep(i); setPhase("interview"); }} onBack={() => setPhase("interview")} onBuild={build} />;

  return (
    <div data-testid="pcp-interview" className="p-6 sm:p-10">
      <div className="flex items-center justify-between text-xs text-[#7A8C82] mb-2">
        <span data-testid="interview-progress">Question {step + 1} of {STEPS.length}</span>
        <span className="flex items-center gap-3">
          {savedAt && <span data-testid="draft-saved-indicator" className="flex items-center gap-1 text-[#4A6B53]"><Check className="w-3 h-3" strokeWidth={2} /> Draft saved</span>}
          <span>{answered} answered</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-[#EAE8E3] overflow-hidden mb-8">
        <div className="h-full bg-[#4A6B53] transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
      </div>

      <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-[0.2em]">{s.section}</p>
      <h2 data-testid="interview-question" className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814] mt-1 leading-tight">{s.question}</h2>
      <p className="mt-2 text-sm text-[#7A8C82] max-w-2xl">{s.hint}</p>

      {s.type === "fields" ? (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          {ABOUT_FIELDS.map(([label, key, type]) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</Label>
              <Input data-testid={`interview-${key}`} type={type} value={about[key] || ""} onChange={(e) => setAbout((a) => ({ ...a, [key]: e.target.value }))}
                className="h-12 bg-[#F9F9F6] border-[#E2DFD8] text-base" />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col lg:flex-row gap-6 items-start">
          <div className="shrink-0 mx-auto lg:mx-0"><Recorder disabled={generating} onTranscript={append} /></div>
          <Textarea data-testid={`interview-${s.key}`} value={answers[s.key] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [s.key]: e.target.value }))}
            rows={7} placeholder="Tap the mic and talk, or type here. Rough notes are fine — I'll clean them up." className="flex-1 bg-[#F9F9F6] border-[#E2DFD8] text-base leading-relaxed resize-y" />
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
        <Button data-testid="interview-back-btn" variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0 || generating} className="h-12 px-5 rounded-full border-[#E2DFD8] bg-white">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back
        </Button>
        <div className="flex gap-2 flex-wrap justify-end">
          {step > 0 && (
            <Button data-testid="interview-review-btn" variant="ghost" onClick={() => setPhase("review")} disabled={generating} className="h-12 px-4 rounded-full text-[#4A6B53]">
              <ClipboardCheck className="w-4 h-4 mr-2" strokeWidth={1.5} /> Review all
            </Button>
          )}
          {!last && s.type !== "fields" && (
            <Button data-testid="interview-skip-btn" variant="ghost" onClick={() => setStep(step + 1)} disabled={generating} className="h-12 px-4 rounded-full text-[#7A8C82]">
              <SkipForward className="w-4 h-4 mr-2" strokeWidth={1.5} /> Skip
            </Button>
          )}
          <Button data-testid="interview-next-btn" onClick={next} disabled={generating || (s.type === "fields" && !nameOk)}
            className="h-12 px-8 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-base font-medium shadow-[0_8px_16px_rgba(74,107,83,0.25)]">
            {last ? <><ClipboardCheck className="w-5 h-5 mr-2" strokeWidth={1.5} /> Review &amp; build</> : <>Next <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
